import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUpRightFromSquare,
  faCopy,
  faCheck,
} from '@fortawesome/free-solid-svg-icons'
import { useState, lazy } from 'react'
import copy from 'copy-to-clipboard'
import { useModal } from '../../../features/modal-manager/index.ts'

const QrCodeModal = lazy(() => import('../QrCodeModal.jsx'))
const EmailLinkModal = lazy(() => import('../EmailLinkModal.jsx'))
const QuickAccessCodeModal = lazy(() => import('../QuickAccessCodeModal.jsx'))

export default function ShareableLinkAndAccessCode({
  url = '',
  loading = false,
  className = '',
  handleRevoke,
  ...props
}) {
  const [linkCopied, setLinkCopied] = useState(false)

  const [qrCodeModal, renderQrCodeModal] = useModal(QrCodeModal)
  const [emailLinkModal, renderEmailLinkModal] = useModal(EmailLinkModal)
  const [quickAccessCodeModal, renderQuickAccessCodeModal] =
    useModal(QuickAccessCodeModal)

  function onCopy() {
    copy(url)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 1500)
  }

  return (
    <div className={['space-y-3', className].join(' ')} {...props}>
      {/* Link */}
      <div className="flex gap-2">
        <div
          className={[
            'grow rounded-sm h-9 text-xs',
            loading
              ? 'bg-animated from-gray-800 to-gray-600 text-white/70'
              : 'bg-gray-800',
          ].join(' ')}
        >
          <input
            key={url} // needed for repaint when switching back from the PDF tab
            defaultValue={url}
            type="text"
            onClick={(event) => event.target.select()}
            readOnly
            disabled={loading}
            className="bg-transparent h-9 w-full px-3 focus:outline-hidden focus:ring-3 rounded-sm"
          />
        </div>
        <button
          className={[
            'rounded-sm text-sm h-9 leading-9 px-3 focus:outline-hidden focus:ring-3 ',
            loading
              ? 'bg-animated from-gray-800 to-gray-600 text-white/70'
              : 'bg-gray-800 hover:bg-gray-700',
          ].join(' ')}
          onClick={onCopy}
        >
          <FontAwesomeIcon
            icon={linkCopied ? faCheck : faCopy}
            size="sm"
            className="mr-2"
          />
          <span>{linkCopied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <div className="flex gap-4">
        {handleRevoke && (
          <button
            className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden focus:ring-3"
            onClick={handleRevoke}
          >
            Expire previous links
          </button>
        )}
        <div className="grow"></div>
        <button
          className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden focus:ring-3"
          onClick={quickAccessCodeModal.show}
        >
          Quick Access Code
        </button>
        <button
          className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden focus:ring-3"
          onClick={qrCodeModal.show}
        >
          QR Code
        </button>
        <button
          className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden focus:ring-3"
          onClick={emailLinkModal.show}
        >
          Email link to guest
        </button>
        <a
          className="text-xs font-semibold text-gray-500 hover:text-gray-300 focus:outline-hidden focus:ring-3"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <span>Open</span>
          <FontAwesomeIcon
            icon={faUpRightFromSquare}
            size="sm"
            className="ml-2"
          />
        </a>
      </div>

      {renderQrCodeModal({
        url,
      })}
      {renderEmailLinkModal({
        url,
      })}
      {renderQuickAccessCodeModal({
        url,
      })}
    </div>
  )
}

ShareableLinkAndAccessCode.propTypes = {
  loading: PropTypes.bool,
  url: PropTypes.string,
  className: PropTypes.string,
  handleRevoke: PropTypes.func,
}
